import type { TrailListItem } from "../api";

/**
 * Saved views for the curator catalog workspace — Phase 2.
 *
 * Each view is a one-click filter over the in-memory trail list. The
 * predicate runs client-side so switching views feels instant (the
 * dashboard pattern from pencilandpaper.io: load once, filter
 * everywhere). At ~280 trails today and a soft ceiling of a few
 * thousand, this stays well under any perceptual cost; we'll
 * push facets back to the worker when the catalog crosses ~5k rows.
 *
 * Names lean trail-friendly per the agreed copy direction; the action
 * verbs ("Closed", "Caution") stay literal so destructive context
 * never gets misread.
 */
export interface SavedView {
  id: string;
  label: string;
  /** Short helper sentence shown under the label on desktop. */
  hint?: string;
  predicate: (t: TrailListItem) => boolean;
}

export const SAVED_VIEWS: SavedView[] = [
  {
    id: "all",
    label: "All trails",
    hint: "Everything in the catalog",
    predicate: () => true,
  },
  {
    id: "needs-pin",
    label: "Needs a pin",
    hint: "Missing coordinates",
    predicate: (t) => t.latitude == null || t.longitude == null,
  },
  {
    id: "unverified",
    label: "Unverified",
    hint: "Haven't been spot-checked yet",
    predicate: (t) => t.verifiedAt == null,
  },
  {
    id: "closed",
    label: "Closed right now",
    hint: "Trails currently shut down",
    predicate: (t) => t.status.value === "closed",
  },
  {
    id: "caution",
    label: "Caution right now",
    hint: "Partially open / use care",
    predicate: (t) => t.status.value === "partially_open",
  },
];

export function SavedViewSidebar({
  active,
  counts,
  onSelect,
}: {
  active: string;
  /** Per-view trail counts — precomputed by the workspace so each
   *  pill can show "(N)" beside the label. Pass an empty map while
   *  the initial load is in flight; we'll just render no count. */
  counts: Record<string, number>;
  onSelect: (viewId: string) => void;
}) {
  return (
    <>
      {/* Desktop sidebar: vertical list of views. Hidden on small
          viewports — the segmented picker below takes over. */}
      <aside className="saved-views-sidebar" aria-label="Saved views">
        <div className="saved-views-eyebrow">Views</div>
        <ul role="list">
          {SAVED_VIEWS.map((v) => {
            const isActive = v.id === active;
            const n = counts[v.id];
            return (
              <li key={v.id}>
                <button
                  type="button"
                  className={`saved-view-btn ${isActive ? "active" : ""}`}
                  onClick={() => onSelect(v.id)}
                  aria-pressed={isActive}
                >
                  <span className="saved-view-label">{v.label}</span>
                  {n !== undefined && (
                    <span className="saved-view-count">{n}</span>
                  )}
                  {v.hint && (
                    <span className="saved-view-hint">{v.hint}</span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </aside>

      {/* Mobile picker: horizontal scroll-snap row of chips. Same view
          set, compact label only. */}
      <nav
        className="saved-views-mobile"
        aria-label="Saved views"
      >
        {SAVED_VIEWS.map((v) => {
          const isActive = v.id === active;
          const n = counts[v.id];
          return (
            <button
              type="button"
              key={v.id}
              className={`saved-view-chip ${isActive ? "active" : ""}`}
              onClick={() => onSelect(v.id)}
              aria-pressed={isActive}
            >
              {v.label}
              {n !== undefined && <span className="chip-count">{n}</span>}
            </button>
          );
        })}
      </nav>
    </>
  );
}
